/** Presentation helpers: colors, time formatting, level normalization. */

/**
 * 8-color palette for container names. Chosen to sit comfortably above WCAG AA
 * (>= 4.5:1) against the dark log background (--bg #0d1117) while staying
 * distinguishable from each other.
 */
export const CONTAINER_PALETTE = [
  '#79c0ff', // blue
  '#56d364', // green
  '#e3b341', // amber
  '#ff9d96', // salmon
  '#d2a8ff', // purple
  '#ff9bce', // pink
  '#76e3ea', // cyan
  '#f0a35e', // orange
];

/** Deterministic palette index for a container name (djb2-ish hash). */
export function containerColor(name: string): string {
  let hash = 5381;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 33) ^ name.charCodeAt(i);
  }
  return CONTAINER_PALETTE[Math.abs(hash) % CONTAINER_PALETTE.length];
}

export type LevelClass = 'error' | 'warn' | 'info' | 'debug' | 'null';

/** Map a raw level string to a CSS class bucket. */
export function levelClass(level: string | null): LevelClass {
  if (!level) return 'null';
  const l = level.toLowerCase();
  if (l === 'error' || l === 'fatal') return 'error';
  if (l === 'warn' || l === 'warning') return 'warn';
  if (l === 'info') return 'info';
  if (l === 'debug' || l === 'trace') return 'debug';
  return 'null';
}

export function levelLabel(level: string | null): string {
  return level ? level.toUpperCase() : '—';
}

function pad(n: number, width = 2): string {
  return String(n).padStart(width, '0');
}

/** Compact wall-clock time for the log row: HH:MM:SS.mmm (local). */
export function shortTime(ts: number): string {
  const d = new Date(ts);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(
    d.getMilliseconds(),
    3,
  )}`;
}

/** Full ISO timestamp for the expanded row. */
export function isoTime(ts: number): string {
  return new Date(ts).toISOString();
}

/** Pretty-print an attrs JSON blob; returns null if empty/unparseable. */
export function prettyAttrs(attrs: string | null): string | null {
  if (!attrs) return null;
  try {
    return JSON.stringify(JSON.parse(attrs), null, 2);
  } catch {
    return attrs;
  }
}
